import { Box, Button, TextField, Typography } from "@mui/material";
import LeftSectionLayout from "components/layout/twoSectionLayout/LeftSectionLayout";
import MainLayoutSection from "components/layout/twoSectionLayout/MainLayoutSection";
import RightSectionLayout from "components/layout/twoSectionLayout/RightSectionLayout";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getSupplierLoginPageDetails,
  getSupplierSurveyLoginOtp,
  getSupplierSurveyLoginOtpValidation,
} from "actions/SupplierSurveyLoginScreenAction/supplierSurveyLogin";
import ContributionSurvey from "components/widgets/phase-3/survey/contributionSurvey/ContributionSurvey";
import { useParams } from "react-router-dom";
import left from "../../../images/phase-3/supplierSurveyLoginImg/leftBack.png";
import glass from "../../../images/phase-3/supplierSurveyLoginImg/glass.png";
import lady from "../../../images/phase-3/supplierSurveyLoginImg/lady.png";

export const LeftLogin = ({ data }) => {
  const { image = {}, survey_name = "" } = data || {};
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Main background image - full height and width */}
      <Box
        component="img"
        src={left}
        alt="background"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          overflow: "hidden",
        }}
      />

      {/* Glass container - centered */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "90%", md: "87%", lg: "85%" },
          maxWidth: "800px",
          aspectRatio: "1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* Glass image */}
        <Box
          component="img"
          src={glass}
          alt="glass"
          sx={{
            position: "absolute",
            width: "100%",
            height: "70%",
            objectFit: "contain",
          }}
        />

        {/* Logo - upper left corner */}
        <Box
          component="img"
          src={image?.url}
          alt="logo"
          sx={{
            position: "absolute",
            top: { xs: "8%", md: "18%" },
            left: { xs: "8%", md: "5%" },
            width: { xs: "50px", sm: "60px", md: "80px" },
            height: "auto",
            objectFit: "contain",
            zIndex: 2,
            borderRadius: "30%",
          }}
        />

        {/* Headline text - middle */}
        <Typography
          variant="h3"
          sx={{
            position: "absolute",
            top: "50%",
            left: "40%",
            transform: "translate(-50%, -50%)",
            color: "white",
            textAlign: "center",
            fontWeight: 700,
            lineHeight: 1,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem", lg: "3rem" },

            width: "70%",
            zIndex: 2,
          }}
        >
          {survey_name}
        </Typography>

        {/* Lady image - right bottom corner inside glass */}
        <Box
          component="img"
          src={lady}
          alt="lady"
          sx={{
            position: "absolute",
            right: { xs: "5%", md: "2%" },
            bottom: { xs: "5%", md: "20%" },
            width: { xs: "30%", sm: "35%", md: "30%" },
            maxWidth: "300px",
            height: "auto",
            objectFit: "contain",
            zIndex: 2,
          }}
        />
      </Box>
    </Box>
  );
};

export const RightLogin = ({ setIsLoggedIn, data }) => {
  const { survey_id: token } = useParams();
  const [showOtp, setShowOtp] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);

  const { isLoading, refetch } = useQuery({
    queryKey: ["otp", email, token, data],
    queryFn: () =>
      getSupplierSurveyLoginOtp({
        email: email.toLowerCase(),
        token,
        entity_name: data?.entity_name,
        supplier_assessment_name: data?.supplier_assessment_name,
      }),
    enabled: false,
  });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendOtp = async () => {
    if (!email) return setEmailError("Email is required");
    if (!validateEmail(email)) return setEmailError("Enter valid email");
    setEmailError("");
    const res = await refetch();
    res?.data?.status && setShowOtp(true);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3)
      document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    const result = await getSupplierSurveyLoginOtpValidation({
      email,
      otp: otp.join(""),
      token,
    });
    if (result?.status) {
      const authObj = {
        token: result.result,
      };

      localStorage.setItem("surveyAccess", btoa(JSON.stringify(authObj)));

      setIsLoggedIn(authObj);
    }
    setLoading(false);
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };
  useEffect(() => {
    if (showOtp) {
      const timer = setTimeout(() => {
        document.getElementById("otp-0")?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showOtp]);

  return (
    <>
      <Box p={1} sx={{ borderBottom: "1px solid #E0E0E0", mb: 2 }}>
        <Typography variant="h3" sx={{ color: "#0F0F0F" }}>
          Login Page
        </Typography>
      </Box>

      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box sx={{ textAlign: "left", mb: 1 }}>
            <Typography variant="h3" sx={{ color: "#0F0F0F", mb: 1 }}>
              Welcome,
            </Typography>
            <Typography variant="body2" sx={{ color: "#9A9A9A" }}>
              To the supplier Assessment portal please log in to complete your
              evaluation survey.
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ color: "#9A9A9A", mb: 1 }}>
              Email Id
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Email Id"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              disabled={showOtp}
            />
          </Box>

          {showOtp && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: "#9A9A9A", mb: 1 }}>
                Enter OTP
              </Typography>

              <Box
                sx={{ display: "flex", gap: 1 }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData("text").trim();
                  if (/^\d{4}$/.test(pasted)) {
                    const digits = pasted.split("");
                    setOtp(digits);
                    setTimeout(() => {
                      document
                        .getElementById(`otp-${digits.length - 1}`)
                        ?.focus();
                    }, 50);
                  }
                }}
              >
                {[0, 1, 2, 3].map((index) => (
                  <TextField
                    key={index}
                    id={`otp-${index}`}
                    value={otp[index]}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    autoComplete="off"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "1.2rem",
                        fontWeight: "550",
                        padding: 0,
                        width: "2.5rem",
                        height: "2.5rem",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {!showOtp ? (
            <Button
              variant="text"
              size="small"
              sx={{ whiteSpace: "nowrap", minWidth: "fit-content", mb: 1 }}
              onClick={handleSendOtp}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send OTP"}
            </Button>
          ) : (
            <Button
              variant="text"
              size="small"
              sx={{ alignSelf: "center", mb: 1 }}
              onClick={() => refetch().then(() => alert("OTP resent!"))}
              disabled={isLoading}
            >
              {isLoading ? "Resending..." : "Resend OTP"}
            </Button>
          )}

          {showOtp && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
            >
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                onClick={handleVerifyOtp}
                disabled={loading || otp.some((val) => !val)}
              >
                {loading ? "Verifying..." : "Login"}
              </Button>
            </Box>
          )}

          <Box sx={{ borderTop: "1px solid #E0E0E0", pt: 2, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#9A9A9A", textAlign: "left", mt: 1, mb: 1 }}
            >
              If login doesn't work please email the client by clicking on the
              below link
            </Typography>
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="text"
                size="small"
                sx={{ whiteSpace: "nowrap", minWidth: "fit-content", mb: 1 }}
              >
                Email Client
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const SupplierSurveyLoginScreen = () => {
  const { survey_id: token } = useParams();
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const saved = localStorage.getItem("surveyAccess");
    if (!saved) return false;
    try {
      const decoded = JSON.parse(atob(saved));
      return decoded;
    } catch {
      return false;
    }
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["otp", token],
    queryFn: () => getSupplierLoginPageDetails({ token }),
    enabled: !!token,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  if (!!isLoggedIn) {
    return (
      <ContributionSurvey
        isLoggedIn={isLoggedIn?.token}
        setLogout={setIsLoggedIn}
        entityLogo={data?.result?.image?.url}
      />
    );
  }
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 4,
      }}
    >
      <MainLayoutSection
        height="90vh"
        leftWidth="49.5%"
        rightWidth="49.5%"
        left={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <LeftSectionLayout padding="0px">
              <LeftLogin data={data?.result} />
            </LeftSectionLayout>
          </Box>
        }
        right={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <RightSectionLayout padding="0px">
              <RightLogin setIsLoggedIn={setIsLoggedIn} data={data?.result} />
            </RightSectionLayout>
          </Box>
        }
      />
    </Box>
  );
};

export default SupplierSurveyLoginScreen;
